import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as alertifyjs from 'alertifyjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UserService } from 'src/app/services/user/user.service';
import { MerchantService } from 'src/app/services/merchant/merchant.service';

/**
 * Nueva venta financiada — versión 2 de `credit-save`.
 *
 * 📍 Por qué existe otra pantalla en vez de tocar la que ya está: la de hoy
 * (`console/credit-save`) es la que usa el negocio todos los días. Esta vive en
 * su propia ruta (`console/credit-save-v2`) para poder compararlas lado a lado
 * con datos reales antes de reemplazar nada.
 *
 * La forma de trabajo está portada de la consola de la pasarela
 * (`pasarela_consola`, rama `gestion-por-comercio`): el punto de venta se
 * confirma arriba y no se vuelve a tocar, el formulario del cliente es uno solo
 * y corto, la simulación se ve completa antes de crear, y el resultado sale en
 * un modal con el código de la operación.
 *
 * 🔴 Los dos servicios son los MISMOS que usa la pantalla vieja:
 *   `clientQuota`  → simula (cupo, plan, inicial y cuotas). No escribe nada.
 *   `creditSave`   → crea el crédito y registra la inicial.
 * Después el cliente tiene que aceptar el pagaré desde su app: mientras no lo
 * acepte, la caja no puede cerrar la venta. Eso es un candado del núcleo, no
 * un paso de esta pantalla.
 */
@Component({
  selector: 'app-credit-save-v2',
  templateUrl: './credit-save-v2.component.html',
  styleUrls: ['./credit-save-v2.component.css'],
})
export class CreditSaveV2Component implements OnInit {
  // ── Punto de venta ────────────────────────────────────────────────────────
  tiendas: any[] = [];
  tiendaSeleccionada: string | null = null;
  cargandoTiendas = false;
  editandoPuntoVenta = false;

  // El ejecutivo es texto libre y vive en localStorage: no sale del token.
  // Se conserva tal cual está hoy para no cambiar el dato que graba el núcleo.
  idEjecutive: any = null;
  formEjecutive: any;
  editandoEjecutivo = false;

  // ── Catálogos ─────────────────────────────────────────────────────────────
  code_identity: any[] = [];
  type_identity: any[] = [];
  plans: any[] = [];
  rate: any;
  rate_load = true;
  rate_error = false;

  // ── Formulario y simulación ───────────────────────────────────────────────
  form: any;
  simulacion: any = null;          // lo que devuelve clientQuota
  monedaSimulada: any = 'USD';
  planSeleccionado: any = null;
  simulando = false;
  creando = false;
  errorMsg: any = null;

  // Ayuda de la pantalla: lo que antes era una columna fija al costado.
  ayudaAbierta = false;

  // ── Modal de resultado (bandera, no NgbModal) ─────────────────────────────
  modalAbierto = false;
  resultado: any = null;           // { exito, mensaje, identificador }

  // ── El cierre de la venta, que son DOS pasos más ──────────────────────────
  // Crear el crédito no cierra nada: queda el pagaré sin aceptar y la inicial
  // sin cobrar. El núcleo no deja registrar la inicial hasta que el pagaré
  // esté aceptado (code 2, "Pagaré no aceptado"), así que el orden es fijo.
  cierre: 'pagare' | 'inicial' | 'listo' | null = null;
  pagarePendiente: any = null;
  personCode: string | null = null;
  buscandoPagare = false;
  aceptandoPagare = false;
  registrandoInicial = false;
  errorCierre: string | null = null;

  sessionUser: any;
  Math = Math;

  constructor(
    private formBuilder: FormBuilder,
    private _user: UserService,
    private router: Router,
    public _auth: AuthService,
    private _merchantSrv: MerchantService
  ) {}

  ngOnInit(): void {
    this._auth.auth$.subscribe((rep: any) => {
      if (rep) {
        this.sessionUser = this._auth.sessionUser;
        if (!this.tiendaSeleccionada) this.preseleccionarTienda();
      }
    });

    if (localStorage.getItem('idEjecutive')) {
      this.idEjecutive = localStorage.getItem('idEjecutive');
    }

    this.formEjecutive = this.formBuilder.group({
      code_identity: ['V', [Validators.required]],
      identity: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
    });

    this.form = this.formBuilder.group({
      type_identity: ['CI', [Validators.required]],
      code_identity: ['V', [Validators.required]],
      currency: ['USD', [Validators.required]],
      identity: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
      total_amount: ['', [Validators.required]],
      plan: [''],
      item: [''],
      invoice: ['', [Validators.required]],
    });

    this.cargarTiendas();
    this.cargarComercios();
    this.getLists();
    this.getPlans();
    this.getRate();
  }

  // ── Carga de datos ────────────────────────────────────────────────────────

  getLists = async () => {
    try {
      const [type_identity, code_identity] = await Promise.all([
        this._user.getPersonTypeIdentity(),
        this._user.getPersonCodeIdentity(),
      ]);
      this.type_identity = type_identity?.resp || [];
      this.code_identity = code_identity?.resp || [];
    } catch (error) {
      console.error(error);
    }
  };

  getPlans = async () => {
    try {
      const plans = await this._user.getplans({});
      this.plans = plans?.res || [];
    } catch (error) {
      console.error(error);
    }
  };

  getRate = async () => {
    this.rate_load = true;
    this.rate_error = false;
    try {
      const respose = await this._user.getRate({ subscriptor: 1 });
      this.rate = respose?.res;
      if (!this.rate) this.rate_error = true;
    } catch (error) {
      console.error(error);
      this.rate_error = true;
    }
    this.rate_load = false;
  };

  /**
   * 🔴 La tienda define a quién se le liquida la venta. No hay valor por
   * omisión escrito a mano: sin tienda elegida, el botón de simular está
   * deshabilitado.
   */
  cargarTiendas = async () => {
    this.cargandoTiendas = true;
    try {
      const rep = await this._user.getshops({});
      this.tiendas = (rep?.res || []).filter((t: any) => t?.code);
      this.preseleccionarTienda();
    } catch (error) {
      console.error(error);
      this.tiendas = [];
    }
    this.cargandoTiendas = false;
  };

  preseleccionarTienda = () => {
    const propia = this.sessionUser?.shop ? String(this.sessionUser.shop) : null;
    if (propia && this.tiendas.some((t: any) => String(t.code) === propia)) {
      this.tiendaSeleccionada = propia;
    } else if (this.tiendas.length === 1) {
      this.tiendaSeleccionada = String(this.tiendas[0].code);
    }
  };

  // ── El comercio de la venta ───────────────────────────────────────────────
  //
  // 🔴 La tienda define A QUIÉN SE LE LIQUIDA. Por eso el comercio se muestra
  // acá, junto a la tienda, y no escondido: una venta de EPA atribuida a
  // MundoTotal no da ningún error, se paga mal y se descubre en la liquidación.
  //
  // 📍 Se muestra el NOMBRE y no el código —«Farmatodo», no «FTD»—: quien carga
  // una venta no tiene por qué saberse los códigos, y un código mal leído es
  // exactamente el error que esto viene a evitar.

  comercios: any[] = [];

  cargarComercios = async (): Promise<void> => {
    try {
      const rep = await this._merchantSrv.getMerchants({ state: 'ACTIVE' });
      this.comercios = rep?.merchants || rep || [];
    } catch { this.comercios = []; }
  };

  get comercioDeLaVenta(): string | null {
    if (!this.tiendaSeleccionada) return null;
    const t = this.tiendas.find((x: any) => String(x.code) === String(this.tiendaSeleccionada));

    // 🔴 LA TIENDA TRAE `merchant`, NO `merchant_code`.
    //
    // Esto leía `t.merchant_code`, que NO EXISTE en lo que devuelve el
    // servidor (verificado: la tienda viene con `code`, `name`, `short_name`,
    // `status` y `merchant` — el identificador, no el código). Así que la
    // búsqueda siempre fallaba y caía al comercio del selector de arriba; con
    // «Todos los comercios» eso es null, y elegir una tienda seguía diciendo
    // «sin comercio». La integración parecía rota y en realidad buscaba por un
    // campo que nunca llega.
    //
    // Ahora se resuelve por identificador contra el catálogo ya cargado, y el
    // resto queda de respaldo por si algún día el back agrega el código.
    const c = t?.merchant
      ? this.comercios.find((x: any) => String(x._id) === String(t.merchant))
      : null;
    if (c) return c.name || c.code;

    const codigo = t?.merchant_code || this._comercioActivoCodigo();
    if (!codigo) return null;
    const porCodigo = this.comercios.find((x: any) => x.code === codigo);
    return porCodigo?.name || codigo;   // si la lista todavía no cargó, el código
  }

  private _comercioActivoCodigo = (): string | null => {
    try { return localStorage.getItem('mc_comercio_activo'); } catch { return null; }
  };

  get tiendaActual(): any {
    if (!this.tiendaSeleccionada) return null;
    return this.tiendas.find((t: any) => String(t.code) === String(this.tiendaSeleccionada)) || null;
  }

  get puntoDeVentaListo(): boolean {
    return !!this.tiendaSeleccionada && !!this.idEjecutive;
  }

  setIdEjecutive = () => {
    const data = this.formEjecutive.getRawValue();
    const ci = data.code_identity + data.identity;
    localStorage.setItem('idEjecutive', ci);
    this.idEjecutive = ci;
    this.editandoEjecutivo = false;
  };

  cambiarEjecutivo = () => {
    this.editandoEjecutivo = true;
  };

  // ── Paso 1: simular ───────────────────────────────────────────────────────

  private armarParams = (): any => {
    const data = this.form.getRawValue();
    const items: any = data.item ? [{ code: data.item }] : [];

    return {
      subscriber: {
        subscriber: 1,
        // El código de tienda va como TEXTO: el núcleo lo busca con
        // Shop.findOne({ code }). Con Number() un código como "EPA-02" da NaN.
        shop: this.tiendaSeleccionada,
        cash_register: 1,
        user: this.idEjecutive,
      },
      client: {
        type_identity: data.type_identity,
        code_identity: data.code_identity,
        identity: data.identity,
      },
      params: {
        plan_id: data.plan,
        currency: data.currency,
        total_amount: data.total_amount,
        chage_rate: this.rate?.amount,
        items: items,
      },
    };
  };

  simular = async () => {
    if (this.simulando) return;
    this.errorMsg = null;
    this.simulacion = null;
    this.simulando = true;

    const data = this.form.getRawValue();
    this.monedaSimulada = data.currency;

    try {
      const resp = await this._user.clientQuota(this.armarParams());
      this.simulacion = resp?.res;
      this.planSeleccionado = this.simulacion?.plan;
    } catch (error: any) {
      const err = error?.error?.error;
      this.errorMsg = err?.msg || error?.error?.msg || 'No se pudo simular el crédito';
    }
    this.simulando = false;
  };

  // ── Paso 2: crear ─────────────────────────────────────────────────────────

  crearCredito = async () => {
    if (this.creando || !this.simulacion) return;
    this.creando = true;

    const data = this.form.getRawValue();
    const params = this.armarParams();

    params.params.merchantNumber = data.invoice;
    params.params.plan_id = this.planSeleccionado?._id;
    params.params.referencesOrder = `${Math.floor(Math.random() * 10) + 1}`;
    // 🔴 Sin `payment_params` a propósito. La inicial se registra en su propio
    // paso, con `register_payment_initial`, que es lo que hace la caja. Mandarla
    // acá no la cobraba igual: verificado en la base, el crédito quedaba con la
    // cuota 0 pendiente y cero pagos.

    try {
      const resp = await this._user.creditSave(params);
      this.resultado = {
        exito: true,
        mensaje: 'El crédito quedó creado. Falta el pagaré y la inicial.',
        identificador: resp?.res?.invoice?.identifier,
      };
      this.personCode = this.simulacion?.person?.code || resp?.res?.person?.code || null;
      this.modalAbierto = true;
      this.cierre = 'pagare';
      this.creando = false;
      await this.buscarPagare();
      return;
    } catch (error: any) {
      const msg = error?.error?.msg || error?.error?.error?.msg || 'No se pudo crear el crédito';
      this.resultado = { exito: false, mensaje: msg, identificador: null };
      this.modalAbierto = true;
      alertifyjs.error(msg);
    }
    this.creando = false;
  };

  // ── Paso 3: el pagaré ─────────────────────────────────────────────────────

  /**
   * El pagaré se busca por CLIENTE, no por factura: el servicio del núcleo
   * (`getPagaresService`) solo acepta `person_code`. De los pendientes se toma
   * el de esta factura.
   */
  buscarPagare = async () => {
    this.errorCierre = null;
    this.pagarePendiente = null;

    // Si la simulación no trajo el código, se resuelve por cédula. Sin código no
    // hay forma de pedir los pagarés: el servicio del núcleo solo busca así.
    if (!this.personCode) {
      const data = this.form.getRawValue();
      try {
        const rep = await this._user.getPersonByIdentifier({
          type_identity: data.type_identity,
          code_identity: data.code_identity,
          identity: data.identity,
        });
        this.personCode = rep?.resp?.code || rep?.res?.code || rep?.person?.code || null;
      } catch { this.personCode = null; }
    }

    if (!this.personCode) {
      this.errorCierre = 'No pude resolver el código del cliente, así que no puedo buscar su pagaré. Aceptalo desde la ficha del cliente.';
      return;
    }

    this.buscandoPagare = true;
    try {
      const resp = await this._user.getPagaresPendientes(this.personCode);
      const lista = resp?.res || resp?.resp || resp || [];
      const delCredito = (lista || []).find((p: any) => p?.invoice?.identifier === this.resultado?.identificador);
      this.pagarePendiente = delCredito || null;

      // Un plan sin pagarés no tiene nada que aceptar: se pasa derecho a la inicial.
      if (!this.pagarePendiente) this.cierre = 'inicial';
    } catch (error: any) {
      this.errorCierre = error?.error?.msg || error?.error?.message || 'No se pudieron consultar los pagarés';
    }
    this.buscandoPagare = false;
  };

  /**
   * ⚠️ En producción esto lo hace EL CLIENTE desde su app. Acá se acepta por él
   * para poder recorrer el circuito completo sin dos dispositivos: es una
   * pantalla de prueba y el pagaré es el candado que la caja no puede saltar.
   */
  aceptarPagare = async () => {
    if (this.aceptandoPagare || !this.pagarePendiente) return;
    this.aceptandoPagare = true;
    this.errorCierre = null;
    try {
      await this._user.acceptPagare({ pagare_id: this.pagarePendiente._id, accepted: true });
      this.cierre = 'inicial';
    } catch (error: any) {
      this.errorCierre = error?.error?.msg || error?.error?.message || 'No se pudo aceptar el pagaré';
    }
    this.aceptandoPagare = false;
  };

  // ── Paso 4: la inicial ────────────────────────────────────────────────────

  get montoInicial(): number {
    return this.simulacion?.total_currency[this.monedaSimulada]?.initiala_amount || 0;
  }

  registrarInicial = async () => {
    if (this.registrandoInicial) return;
    this.registrandoInicial = true;
    this.errorCierre = null;

    const data = this.form.getRawValue();
    const params = this.armarParams();
    params.params.id_transaction = this.resultado?.identificador;
    params.params.merchantNumber = data.invoice;
    params.params.referencesOrder = `${Math.floor(Math.random() * 10) + 1}`;
    params.params.payment_params = [
      { amount: this.montoInicial, chage_rate: this.rate?.amount, currency: this.monedaSimulada }
    ];

    try {
      const resp: any = await this._user.registerPaymentInitial(params);
      // El núcleo contesta 200 con un error adentro en varios casos.
      const err = resp?.error || resp?.res?.error;
      if (err) {
        // 42 = ya tenía la inicial pagada · 43 = el plan no lleva inicial.
        if (err.code === 42 || err.code === 43) this.cierre = 'listo';
        else this.errorCierre = err.msg || 'No se pudo registrar la inicial';
      } else {
        this.cierre = 'listo';
      }
    } catch (error: any) {
      const err = error?.error?.error || error?.error;
      this.errorCierre = err?.msg || err?.message || 'No se pudo registrar la inicial';
    }
    this.registrandoInicial = false;

    // Enviada la inicial, la caja no tiene nada más que hacer acá: se va a la
    // ficha del cliente, que es donde se ve el crédito y su estado. Si el
    // cliente todavía tiene que aprobar algo, lo hace desde su teléfono.
    if (this.cierre === 'listo') this.salirAlCliente();
  };

  // ══ CARGAR LA INICIAL DE UN CRÉDITO YA CREADO ═════════════════════════════
  //
  // 📍 Esto NO existía en la consola: el paso suelto de «pago inicial» es del
  // integrador de caja (el gateway expone los tres servicios por separado:
  // consulta, crear crédito y pago inicial). Acá el único camino era crear el
  // crédito y seguir de largo, así que un crédito que quedó a medias —pagaré
  // aceptado tarde, caja que cerró antes— no se podía completar desde la
  // consola. Este atajo es para eso.

  modalSuelta = false;
  sueltaForm: any;
  enviandoSuelta = false;
  errorSuelta: string | null = null;
  okSuelta: string | null = null;

  abrirInicialSuelta = () => {
    const data = this.form?.getRawValue();
    this.sueltaForm = this.formBuilder.group({
      code_identity: [data?.code_identity || 'V', [Validators.required]],
      type_identity: [data?.type_identity || 'CI', [Validators.required]],
      identity: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
      identifier: ['', [Validators.required]],
      currency: ['USD', [Validators.required]],
      amount: ['', [Validators.required]],
    });
    this.errorSuelta = null;
    this.okSuelta = null;
    this.modalSuelta = true;
  };

  cerrarInicialSuelta = () => {
    this.modalSuelta = false;
  };

  enviarInicialSuelta = async () => {
    if (this.enviandoSuelta) return;
    this.enviandoSuelta = true;
    this.errorSuelta = null;
    this.okSuelta = null;

    const d = this.sueltaForm.getRawValue();
    const params = {
      subscriber: { subscriber: 1, shop: this.tiendaSeleccionada, cash_register: 1, user: this.idEjecutive },
      client: { type_identity: d.type_identity, code_identity: d.code_identity, identity: d.identity },
      params: {
        id_transaction: (d.identifier || '').trim(),
        referencesOrder: `${Math.floor(Math.random() * 10) + 1}`,
        payment_params: [{ amount: Number(d.amount), chage_rate: this.rate?.amount, currency: d.currency }],
      },
    };

    try {
      const resp: any = await this._user.registerPaymentInitial(params);
      const err = resp?.error || resp?.res?.error;
      if (err && err.code !== 42 && err.code !== 43) {
        this.errorSuelta = err.msg || 'No se pudo registrar la inicial';
      } else {
        this.okSuelta = err?.code === 42
          ? 'Ese crédito ya tenía la inicial pagada.'
          : err?.code === 43
            ? 'Ese plan no lleva inicial: no hay nada que cobrar.'
            : 'Inicial registrada.';
      }
    } catch (error: any) {
      const e = error?.error?.error || error?.error;
      this.errorSuelta = e?.msg || e?.message || 'No se pudo registrar la inicial';
    }
    this.enviandoSuelta = false;
  };

  private salirAlCliente = () => {
    this.modalAbierto = false;
    const destino = this.personCode ? `/console/person/profile/${this.personCode}` : '/console/ventas';
    this.router.navigate([destino]);
  };

  // ── Utilidades de pantalla ────────────────────────────────────────────────

  volverASimular = () => {
    this.simulacion = null;
    this.errorMsg = null;
  };

  limpiar = () => {
    this.form.patchValue({
      identity: '',
      total_amount: '',
      plan: '',
      item: '',
      invoice: '',
    });
    this.simulacion = null;
    this.errorMsg = null;
    this.resultado = null;
  };

  cerrarModal = () => {
    this.modalAbierto = false;
    this.cierre = null;
    this.pagarePendiente = null;
    this.errorCierre = null;
    if (this.resultado?.exito) this.limpiar();
  };

  /**
   * 🔴 Crear un credito es plata que sale: la pantalla es solo para Gerencia.
   * El permiso `compras_financiadas.credito.create` lo declara el nucleo en
   * helpers/rbac-seed.js y hoy lo toma unicamente GERENCIA_CREDITO_COBRANZA
   * (por su comodin *.*.*); los roles con lista explicita no lo tienen.
   *
   * ⚠️ Ojo con la otra capa: `hasPermission` devuelve true cuando el usuario
   * NO tiene RBAC (auth.service.ts:469), asi que el freno de verdad para los
   * usuarios viejos es el `data.profiles` de la ruta.
   */
  get esGerente(): boolean {
    return this._auth.hasPermission('compras_financiadas.credito.create');
  }

  get nombreCliente(): string {
    const p = this.simulacion?.person;
    if (!p) return '';
    return `${p?.name || ''} ${p?.lastName || ''}`.trim();
  }

  get cuotas(): any[] {
    return (this.simulacion?.installments || []).filter((i: any) => i?.order >= 1);
  }
}
